import { Controller } from "@hotwired/stimulus"
import { OnboardingApi } from "../../services/onboarding_api"
import { formatBytes, escapeHtml } from "../../utils/html_helpers"

// File upload controller - handles supporting document uploads
export default class extends Controller {
  static values = { sessionId: String }
  
  static targets = ["fileList", "fileEmptyState"]
  
  static outlets = ["onboarding-wizard"]

  connect() {
    this.api = new OnboardingApi(this.sessionIdValue)
  }

  handleFileChange(event) {
    if (!this.hasFileListTarget) return

    const files = Array.from(event.target.files || [])

    if (!files.length) {
      this.fileListTarget.innerHTML = ""
      if (this.hasFileEmptyStateTarget) {
        this.fileEmptyStateTarget.classList.remove("hidden")
      }
      this.updateSupportingDocs([])
      return
    }

    const itemsHtml = files
      .map(file => {
        return `<li data-filename="${escapeHtml(file.name)}">
          ${escapeHtml(file.name)}
          <span class="text-xs text-theme-muted">(${formatBytes(file.size)})</span>
        </li>`
      })
      .join("")

    this.fileListTarget.innerHTML = itemsHtml

    if (this.hasFileEmptyStateTarget) {
      this.fileEmptyStateTarget.classList.add("hidden")
    }

    this.updateSupportingDocs(
      files.map(file => ({ filename: file.name, size: file.size }))
    )
  }

  updateSupportingDocs(docsArr) {
    // Notify wizard controller to update its state
    this.dispatch("docs-updated", { detail: { docs: docsArr } })
  }

  async recordKnowledge(event) {
    event.preventDefault()

    if (!this.hasFileListTarget) return

    const realFileItems = Array.from(this.fileListTarget.children || []).filter(
      li => li.dataset && li.dataset.filename
    )

    // No files – just keep state clean and show empty message
    if (!realFileItems.length) {
      if (this.hasFileEmptyStateTarget) {
        this.fileEmptyStateTarget.classList.remove("hidden")
      }
      this.dispatch("summary-updated", { detail: { summary: "" } })
      return
    }

    // One status line, replaced as the call progresses. It used to say
    // "Docs summarised" before the request was even sent, and a failure went
    // only to the console, so a dead summary path looked finished (F1 slice 1).
    this.fileListTarget.querySelectorAll("[data-ai-summary]").forEach(li => li.remove())
    this.fileListTarget.insertAdjacentHTML(
      "beforeend",
      `<li class="text-theme-muted" data-ai-summary="true">Summarising your documents…</li>`
    )
    const status = this.fileListTarget.querySelector("[data-ai-summary]")

    try {
      // Get payload from wizard controller
      const payload = this.getIcpPayload()
      const data = await this.api.summarizeIcp(payload)

      if (data.icp_summary_text) {
        status.className = "text-theme-primary font-semibold"
        status.textContent = `Docs summarised · ${new Date().toLocaleTimeString()}`
        this.dispatch("summary-updated", { detail: { summary: data.icp_summary_text } })
      } else {
        this.showSummaryFailure(status, "The server returned no summary.")
      }
    } catch (error) {
      console.error('[fileUpload] Summarize error:', error)
      this.showSummaryFailure(status, this.serverMessage(error))
    }
  }

  showSummaryFailure(status, message) {
    status.className = "text-red-500"
    status.setAttribute("role", "alert")
    status.textContent = `The AI summary failed: ${message} Your documents are still attached; use Summarise again to retry.`
  }

  // OnboardingApi#post throws `HTTP <status>: <body>`; the body carries the
  // message the server wrote for the person, so show that rather than the code.
  serverMessage(error) {
    const body = String(error && error.message || "").replace(/^HTTP \d+:\s*/, "")
    try {
      const parsed = JSON.parse(body)
      if (parsed && parsed.error) return parsed.error
    } catch (_) { /* not JSON, fall through */ }
    return body || "no reason was given."
  }

  // Get ICP payload from wizard controller
  getIcpPayload() {
    if (this.hasOnboardingWizardOutlet) {
      return this.onboardingWizardOutlet.buildIcpPayload()
    }
    return {}
  }
}
